import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// @mui material components
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
/* import DataTable from "examples/Tables/DataTable"; */

import { getOrganisms, deleteOrganism } from "../../../../api/organisms";

import DeleteModal from "../../../../customComponents/devicesComponents/DeleteModal";

import CreateEditOrganism from "../../../../customComponents/adminComponents/CreateEditOrganism";

import { setAllOrganisms } from "../../../../redux/reducers/organismsReducer";
import { useDispatch } from "react-redux";

export default function Organisms() {
  useAuthCheck();
  const { user } = useAuth();

  const [organisms, setOrganisms] = useState(null);
  const [page, setPage] = useState(1);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const [controller] = useMaterialUIController();
  const { darkMode, cardBoard } = controller;

  async function fetchOrganisms() {
    if (user) {
      const response = await getOrganisms();
      setOrganisms(response.data);
      const formatedOrganisms = await response.data.map((organism) => ({
        label: organism.organism_name,
        id: organism.organism_id,
      }));
      dispatch(setAllOrganisms(formatedOrganisms));
    }
  }

  async function deleteOrganismAPI(organism) {
    await deleteOrganism(organism);
  }

  function paginate(array, page_size, page_number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  async function submit(organism) {
    await deleteOrganismAPI(organism);
    await fetchOrganisms();
  }

  useEffect(() => {
    if (user && user.role !== "Admin" && user.role !== "Pilote") {
      navigate("/accueil");
    } else {
      fetchOrganisms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={1.5}>
        <Grid container justifyContent="center" direction="row">
          <Grid item sx={{ padding: 1, marginTop: 3 }} lg={3} md={6} sm={6} xs={12}>
            <CreateEditOrganism organisms={organisms} setOrganisms={setOrganisms} edit={false} />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {organisms ? (
            !cardBoard ? (
              paginate(organisms, 12, page).map((organism) => (
                <Grid item mb={1.5} lg={3} md={6} xs={12}>
                  <Card sx={{ padding: "0 1rem" }}>
                    <MDBox
                      mx={0.5}
                      mt={-3}
                      py={3}
                      px={2}
                      sx={{
                        background: !darkMode
                          ? "linear-gradient(195deg, #364c70, #273956)"
                          : "white",
                        boxShadow: `0 1px 4px 1px ${!darkMode ? "#344767" : "white"}`,
                      }}
                      variant="gradient"
                      borderRadius="lg"
                    >
                      <MDTypography
                        variant="h5"
                        textAlign="center"
                        color={!darkMode ? "white" : "#344767"}
                      >
                        {organism.organism_name ? organism.organism_name : "Nom inconnu"}
                      </MDTypography>
                    </MDBox>
                    <MDBox textAlign="center" pb={3} pt={3}>
                      <Grid
                        container
                        rowSpacing={1.5}
                        columnSpacing={{ xs: 2 }}
                        justifyContent="center"
                      >
                        <Grid item>
                          <CreateEditOrganism
                            organisms={organisms}
                            setOrganisms={setOrganisms}
                            organism={organism}
                            edit
                          />
                        </Grid>
                        <Grid item>
                          <DeleteModal
                            message={`l'organisme ${organism.organism_name}`}
                            onOk={() => {
                              submit(organism.organism_id);
                            }}
                            type="organism"
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              ))
            ) : (
              paginate(organisms, 12, page).map((organism) => (
                <Grid xs={12} sx={{ paddingTop: "0px !important", marginBottom: "0px !important" }}>
                  <Grid item mt={0.5} mb={0.5} xs={12}>
                    <Divider
                      sx={{
                        margin: "0.5rem 0rem",
                        background: !darkMode ? "#000" : "#FFF",
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
                  >
                    <Grid mt={0.75} item lg={2.5} xs={12}>
                      <MDTypography variant="h5" textAlign="center">
                        {organism.organism_name ? organism.organism_name : "Nom inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <CreateEditOrganism
                        organisms={organisms}
                        setOrganisms={setOrganisms}
                        organism={organism}
                        edit
                      />
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <DeleteModal
                        message={`l'organisme ${organism.organism_name}`}
                        onOk={() => {
                          submit(organism.organism_id);
                        }}
                        type="organism"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))
            )
          ) : (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Aucun organisme à afficher
              </MDTypography>
            </Grid>
          )}
          {organisms &&
            (organisms.length <= 12 ? (
              <></>
            ) : (
              <Grid container xs={12} mt={3} justifyContent="center">
                <Stack spacing={2}>
                  <Pagination
                    page={page}
                    onChange={handlePageChange}
                    variant="text"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      button: {
                        color: !darkMode ? "#1A2035" : "#FFF",
                      },
                      "button.Mui-selected": { color: !darkMode ? "#FFF" : "#1A2035" },
                    }}
                    count={Math.ceil(organisms.length / 12)}
                    color={darkMode ? "white" : "dark"}
                  ></Pagination>
                </Stack>
              </Grid>
            ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
